import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OfficeLayout from '../../components/OfficeLayout';
import { StatusBadge } from '../../components/StatusBadge';
import { getRecords, resendEmail, formatApiError } from '../../utils/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { Mail, RefreshCw } from 'lucide-react';

export default function EmailExceptions() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [msg, setMsg] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await getRecords({ status: 'email_exception' });
      setRecords(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { refresh } = useAutoRefresh(load, 20000);

  const handleResend = async (id) => {
    setActionLoading(id); setMsg('');
    try {
      await resendEmail(id);
      setMsg('Email resent successfully!');
      load();
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setActionLoading(''); }
  };

  const ref = (r) => r.document_type === 'trip_sheet' ? r.trip_sheet_fields?.trip_sheet_no || '—' : r.waybill_fields?.waybill_no || '—';

  return (
    <OfficeLayout>
      <div data-testid="email-exceptions-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-[#1A1A2E]">Email Exceptions</h2>
            <p className="text-sm text-[#6B7280] mt-1">Records where proof email failed to send</p>
          </div>
          <button onClick={() => refresh(true)} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1A1A2E]"><RefreshCw size={14} /> Refresh</button>
        </div>

        {msg && <div className="mb-4 px-4 py-3 rounded-lg border text-sm bg-blue-50 border-blue-200 text-blue-700">{msg}</div>}

        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div></div>
          ) : records.length === 0 ? (
            <div className="text-center py-16 text-[#6B7280]">
              <Mail className="mx-auto mb-3 text-[#94A3B8]" size={40} />
              <p>No email exceptions. All emails sent successfully.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Ref No.</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Type</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Email</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Error</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Signed At</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-3 font-semibold text-[#1A1A2E]">{ref(r)}</td>
                    <td className="px-5 py-3 capitalize text-[#6B7280]">{r.document_type.replace('_', ' ')}</td>
                    <td className="px-5 py-3 text-[#1A1A2E]">{r.email_address}</td>
                    <td className="px-5 py-3 text-red-600 text-xs">{r.email_error || 'Unknown'}</td>
                    <td className="px-5 py-3 text-[#6B7280]">{r.signed_at ? new Date(r.signed_at).toLocaleString() : '—'}</td>
                    <td className="px-5 py-3 flex gap-2">
                      <button onClick={() => handleResend(r.id)} disabled={actionLoading === r.id} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium disabled:opacity-50" data-testid={`resend-${r.id}`}><Mail size={12} /> Resend</button>
                      <button onClick={() => navigate(`/record/${r.id}`)} className="text-[#0D9488] text-xs font-medium">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </OfficeLayout>
  );
}
